import { Targeting } from "./interfaces/TargetingProvider";
import { AdSizeMap } from "./viewability/Viewability";

export type ValidOptionsValue = string | null | number | object | boolean | undefined |
    Array<ValidOptionsValue>;

export type AdEvents = "ON_RENDER" | "ON_BEFORE_RENDER" | "ON_EMPTY" | "ON_LOAD";

export interface SlotRenderEndedEvent {
    getAdId: () => string;
    getAdCode: () => string;
    getLineItemId: () => string;
    getCreativeId: () => string;
    isEmpty: () => boolean;
    getRequestTime: () => Date;
    getSize: () => number[][];
}

export interface EventTarget {
    dispatchEvent: (eventType: string, details: SlotRenderEndedEvent) => null;
}

export interface Ad extends EventTarget {
    addTargeting: (targeting: Record<string, ValidOptionsValue>) => Record<string, ValidOptionsValue>;
    EVENTS: AdEvents;
    getContainerId: () => string;
    getDomId: () => string | null;
    getElement: () => HTMLElement | null;
    getElementTopPosition: () => number;
    getId: () => string;
    getIframe: () => HTMLElement | null;
    getPerformanceId: () => string;
    getSizes: () => Array<number>;
    getSlot: () => object | null;
    getTargeting: () => object;
    getUnitName: () => string;
    getViewportOffset: () => number;
    hasSlot: () => boolean;
    isDisplayable: () => boolean;
    isInterstitial: () => boolean;
    isMobile: () => boolean;
    onEmpty: (callback: () => void) => void;
    onRender: (callback: () => void) => void;
    setClassName: (className: string) => Ad;
    setDimensions: (width: string, height: string) => Ad;
    setHeight: (height: string) => Ad;
    setInterstitialFlag: (flag: boolean) => Ad;
    setSlot: (slot: object) => Ad;
    setSlotRenderedEvent: (event: SlotRenderEndedEvent) => void;
    setViewportOffset: (offset: number) => Ad;
    getSlotRenderedEvent: () => SlotRenderEndedEvent;
    handleSlotOnLoadEvent: (event:object, iframe?: HTMLIFrameElement) => void;
    handleSlotRenderEvent: (event: SlotRenderEndedEvent) => void;
    handleTaglessRenderEvent: (event: SlotRenderEndedEvent) => void;
}

type CustomAttribute = string | number | boolean | null | undefined;
type CustomAttributes = { [key: string]: CustomAttribute };

export interface NewrelicBrowser {
    addToTrace: (trace: Record<string, unknown>) => void;
    addPageAction: (name: string, customAttributes?: CustomAttributes) => void;
    noticeError: (error: Error, customAttributes?: CustomAttributes) => void;
    setCustomAttribute: (
        name: string,
        value: CustomAttribute,
        persist?: boolean,
    ) => void;
}

export interface RegUserInfo {
    sessionId?: string;
    userId?: string
}

export interface RegUIClient {
    isTerminalSubscriber?: () => boolean;
    isBBGSubscriber?: () => boolean;
}

export type OnDvTagReadyCb = null | (() => void);
export type SimpleCb = () => void;
export type TargetingDecorator = (targeting: Targeting) => Targeting;
export type SparkleStub = {
    cmd?: {
        push: (cb: SimpleCb) => void;
    };
}

type BomboraCb = (data: Record<string, unknown>) => unknown;
interface Bombora {
    (name: "vi", cb: BomboraCb): void;
}

declare global {
    const dvtag: {
        cmd: (() => void)[],
        queueAdRequest: (opts: { callback?: OnDvTagReadyCb }) => void;
    };
    interface Window {
        __bloomberg__?: { regUIClient: RegUIClient, adMetrics: Record<string, unknown> };
        _regUserInfo: RegUserInfo;
        NREUM: NewrelicBrowser;
        Spritz: {
            cmd: { push: (cb: () => void) => void };
            inject: (src: string, opts: Record<string, unknown>) => void;
        };
        onDvtagReady: (cb?: OnDvTagReadyCb, timeout?: number) => void;
        dvtag?: object;
        _bmb?: Bombora;
    }
}

export interface AdContextData {
    slug?: string;
    adPageType?: string;
    adCode?: string;
    tickerId?: string;
    // this field does not exist in foundation because it is used
    // by shi-chang, which does not use foundation at this point
    personId?: string;
    suid?: string;
}

export const TARGETING_TYPES = {
    IVT: "ivt",
    DV: "dv",
    PERSONALIZATION: "personalization",
    DV_BRANDSAFETY: "dv_brandsafety",
    CONTENT: "content",
    TRIGR: "tr"
} as const;

export type ExcludeTargetingType = typeof TARGETING_TYPES[keyof typeof TARGETING_TYPES]

export interface SparkleInitOptions {
    disableTrigr?: boolean;
    useCachedConsent: boolean;
    adBlockerShouldBeBlocked?: boolean;
    breakpoints: Record<string, number>;
    networkCode: string;
    adCodeDecorator?: (adCode: string) => string;
    pageTargeting?: { page?: string } & Targeting;
    trigrUrl?: string;
    trigrDeadline?: number;
    observerOptions?: IntersectionObserverInit;
    disableIAS?: boolean;
    dvScriptUrl?: string;
    adContextData?: AdContextData
    watchlistUrl?: string;
    isDecoupled?: boolean;
    application?: string;
    enableEventRecorder?: boolean;
    excludeTargetingTypes?: ExcludeTargetingType[]
}

export interface AdOptions {
    containerId: string;
    adCode: string;
    dimensions: AdSizeMap;
    targeting: Targeting;
    strategy: string;
    viewportOffset?: number;
    targetingModifiers?: TargetingDecorator[];
    isInterstitial?: boolean;
    isDecoupled?: boolean;
    shadowHostElement?: HTMLElement;
}

export interface Destructible {
    destroy: () => void;
}
